/**
 * Subscription utility functions for the frontend
 * Extracts subscription tier and limits from JWT token
 */

import { decodeJWTToken } from './auth';

/**
 * Get user's subscription information from JWT token
 * @returns {object} Subscription information including tier, limits, and features
 */
export const getSubscriptionInfo = () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return {
        tier: 'free',
        subscription_tier: 'free', // Add this for compatibility
        limits: { calendar_view_days: 4 },
        features: ['basic_clients', 'basic_appointments'],
        status: 'inactive',
        subscription_status: 'inactive' // Add this for compatibility
      };
    }

    const decoded = decodeJWTToken(token);
    if (!decoded) {
      return {
        tier: 'free',
        subscription_tier: 'free', // Add this for compatibility
        limits: { calendar_view_days: 4 },
        features: ['basic_clients', 'basic_appointments'],
        status: 'inactive',
        subscription_status: 'inactive' // Add this for compatibility
      };
    }

    return {
      tier: decoded.subscription_tier || 'free',
      subscription_tier: decoded.subscription_tier || 'free', // Add this for compatibility
      limits: decoded.limits || { calendar_view_days: 4 },
      features: decoded.features || ['basic_clients', 'basic_appointments'],
      status: decoded.subscription_status || 'inactive',
      subscription_status: decoded.subscription_status || 'inactive', // Add this for compatibility
      expires_at: decoded.subscription_expires_at
    };
  } catch (error) {
    console.error('Error getting subscription info:', error);
    return {
      tier: 'free',
      subscription_tier: 'free', // Add this for compatibility
      limits: { calendar_view_days: 4 },
      features: ['basic_clients', 'basic_appointments'],
      status: 'inactive',
      subscription_status: 'inactive' // Add this for compatibility
    };
  }
};

/**
 * Check if user has a specific feature
 * @param {string} feature - Feature to check
 * @returns {boolean} True if user has access to the feature
 */
export const hasFeature = (feature) => {
  const subscription = getSubscriptionInfo();
  return subscription.features.includes(feature);
};

/**
 * Check if user's tier meets minimum requirement
 * @param {string} requiredTier - Minimum tier required
 * @returns {boolean} True if user's tier meets requirement
 */
export const hasTierAccess = (requiredTier) => {
  const tierLevels = {
    free: 0,
    basic: 1,
    pro: 2,
    enterprise: 3,
    internal: 4
  };

  const subscription = getSubscriptionInfo();
  const userLevel = tierLevels[subscription.tier] || 0;
  const requiredLevel = tierLevels[requiredTier] || 0;

  return userLevel >= requiredLevel;
};

/**
 * Get calendar access limitations based on subscription tier
 * @returns {object} Calendar limitations
 */
export const getCalendarLimits = () => {
  const subscription = getSubscriptionInfo();
  const calendarViewDays = subscription.limits.calendar_view_days || 4;

  const isLimited = calendarViewDays !== -1;

  return {
    calendar_view_days: calendarViewDays,
    is_limited: isLimited,
    tier: subscription.tier,
    description: isLimited
      ? `Calendar view limited to today only (${subscription.tier} tier)`
      : `Unlimited calendar access (${subscription.tier} tier)`
  };
};

/**
 * Calculate allowed date range for calendar based on tier
 * @param {Date} requestedStartDate - Optional requested start date
 * @param {Date} requestedEndDate - Optional requested end date
 * @returns {object} Allowed date range and metadata
 */
export const getCalendarDateRange = (requestedStartDate = null, requestedEndDate = null) => {
  const subscription = getSubscriptionInfo();
  const calendarViewDays = subscription.limits.calendar_view_days || 4;

  // If unlimited access, return requested dates or reasonable defaults
  if (calendarViewDays === -1) {
    if (requestedStartDate && requestedEndDate) {
      return {
        startDate: requestedStartDate,
        endDate: requestedEndDate,
        wasLimited: false,
        tier: subscription.tier
      };
    }

    // Default to current month for unlimited access
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    return {
      startDate: startOfMonth,
      endDate: endOfMonth,
      wasLimited: false,
      tier: subscription.tier
    };
  }

  // For limited access (free tier), calculate "Today Only"
  const now = new Date();

  // Calculate today
  const allowedStart = new Date(now);
  allowedStart.setHours(0, 0, 0, 0);

  const allowedEnd = new Date(now);
  allowedEnd.setHours(23, 59, 59, 999);

  // If requested dates are provided, check if they need to be limited
  let wasLimited = false;
  if (requestedStartDate && requestedEndDate) {
    wasLimited = requestedStartDate < allowedStart || requestedEndDate > allowedEnd;
  }

  return {
    startDate: allowedStart,
    endDate: allowedEnd,
    wasLimited,
    tier: subscription.tier,
    calendarViewDays
  };
};

/**
 * Check if a specific date is allowed for viewing based on tier
 * @param {Date} checkDate - Date to check
 * @returns {boolean} True if date is allowed
 */
export const isDateAllowedForTier = (checkDate) => {
  const subscription = getSubscriptionInfo();
  const calendarViewDays = subscription.limits.calendar_view_days || 4;

  // Unlimited access
  if (calendarViewDays === -1) {
    return true;
  }

  // Check if the date falls within the allowed range
  const { startDate, endDate } = getCalendarDateRange();

  return checkDate >= startDate && checkDate <= endDate;
};

/**
 * Get upgrade message for tier limitations
 * @returns {string} Message encouraging upgrade
 */
export const getUpgradeMessage = () => {
  const subscription = getSubscriptionInfo();

  if (subscription.tier === 'basic' || subscription.tier === 'free') {
    return 'Upgrade to Pro or Enterprise for unlimited calendar access and more features.';
  }

  return '';
};

/**
 * Check if user should see upgrade prompts
 * @returns {boolean} True if user should see upgrade prompts
 */
export const shouldShowUpgradePrompts = () => {
  const subscription = getSubscriptionInfo();
  return ['free', 'basic'].includes(subscription.tier);
};

/**
 * Get appointment access scope based on tier
 * @returns {object} { scope: 'today' | 'all' }
 */
export const getAppointmentAccessScope = () => {
  const subscription = getSubscriptionInfo();

  // Free tier only sees today's appointments
  if (subscription.tier === 'free') {
    return {
      scope: 'today',
      description: 'View today\'s appointments only'
    };
  }

  return {
    scope: 'all',
    description: 'View all appointments'
  };
};

/**
 * Get client access scope based on tier
 * @returns {object} { scope: 'today_appointments_only' | 'all' }
 */
export const getClientAccessScope = () => {
  const subscription = getSubscriptionInfo();

  // Free tier only sees clients who have appointments today
  if (subscription.tier === 'free') {
    return {
      scope: 'today_appointments_only',
      description: 'View clients with appointments today only'
    };
  }

  return {
    scope: 'all',
    description: 'View all clients'
  };
};

/**
 * Get document access limits based on tier
 * @returns {object} { scope: 'today_appointments_clients' | 'all', limit: number | Infinity }
 */
export const getDocAccessLimits = () => {
  const subscription = getSubscriptionInfo();

  if (subscription.tier === 'free') {
    return {
      scope: 'today_appointments_clients',
      limit: null, // "View... records for those clients" implies all records for visible clients, or maybe a number limit? Request says "view... records for those clients". Assuming no hard count limit for the *visible* clients, but strictly scoped to those clients.
      description: 'Records for today\'s clients only'
    };
  }

  if (subscription.tier === 'pro') {
    return {
      scope: 'all',
      limit: 1000,
      description: 'View all client docs (limit 1,000 total)'
    };
  }

  return {
    scope: 'all',
    limit: Infinity,
    description: 'Unlimited document access'
  };
}; 